import React, { useState, useEffect } from 'react';
import { api } from '../../api';
import { colors, fontFamily } from '../../theme';
import { SPORTS_CONFIG } from '../../config/sportsConfig';
import ToastHost, { useToast } from '../shared/Toast';

export default function GameAdminSection() {
  const { toast, showToast } = useToast();
  const [bookSports, setBookSports] = useState(undefined); // ceiling — null = book allows all
  const [agentSports, setAgentSports] = useState(null); // this agent's own narrowing — null = inherit book fully
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      const data = await api.getMyAgentSports();
      setBookSports(data.book_sports);
      setAgentSports(data.agent_sports);
      setLoading(false);
    })();
  }, []);

  const selectableSports = bookSports
    ? Object.keys(SPORTS_CONFIG).filter(k => bookSports.includes(k))
    : Object.keys(SPORTS_CONFIG);

  const saveAgentSports = async () => {
    setSaving(true);
    try {
      await api.updateMyAgentSports(agentSports);
      showToast('Sports settings saved');
    } catch (err) {
      showToast(err.response?.data?.error || 'Failed to save sports settings', 'error');
    } finally {
      setSaving(false);
    }
  };

  const sectionCard = { background: colors.cardBg, boxShadow: colors.shadowCard, borderRadius: 8, marginBottom: '1rem' };
  const saveButtonStyle = { background: colors.goldActiveBg, border: `1px solid ${colors.gold}`, color: colors.textPrimary, borderRadius: 6, padding: '0.6rem 1.5rem', cursor: 'pointer', fontWeight: 700, fontFamily };
  const pillButtonStyle = (active) => ({ background: active ? colors.cardBgActive : colors.sectionBg, border: `1px solid ${colors.goldMuted}`, borderRadius: 6, padding: '0.3rem 0.75rem', color: colors.textPrimary, cursor: 'pointer', fontSize: '0.8rem', fontFamily });

  if (loading) {
    return <p style={{ color: colors.textMuted, fontFamily }}>Loading...</p>;
  }

  return (
    <div style={{ color: colors.textPrimary, fontFamily }}>
      <ToastHost toast={toast} />
      <h2 style={{ color: colors.textPrimary, marginBottom: '1rem' }}>Game Admin</h2>

      <div style={{ ...sectionCard, padding: '1.25rem' }}>
        <h3 style={{ marginTop: 0, marginBottom: '0.75rem' }}>Enabled Sports</h3>
        <p style={{ color: colors.textMuted, fontSize: '0.85rem', marginBottom: '0.75rem' }}>
          Choose which of your book's enabled sports your customers can bet on. Leaving all unchecked enables everything your book allows.
        </p>
        <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.75rem', flexWrap: 'wrap' }}>
          <button onClick={() => setAgentSports(null)} style={pillButtonStyle(!agentSports)}>All Sports</button>
          <button onClick={() => setAgentSports([])} style={pillButtonStyle(false)}>None</button>
        </div>
        {selectableSports.length === 0 ? (
          <p style={{ color: colors.textFaint, fontSize: '0.85rem', marginBottom: '0.75rem' }}>Your book has no sports enabled yet.</p>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '0.4rem', maxHeight: 320, overflowY: 'auto', marginBottom: '0.75rem' }}>
            {selectableSports.map((key) => {
              const cfg = SPORTS_CONFIG[key];
              const isEnabled = !agentSports || agentSports.includes(key);
              return (
                <label key={key} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.4rem 0.6rem', borderRadius: 6, background: isEnabled ? colors.cardBgActive : colors.sectionBg, border: `1px solid ${isEnabled ? colors.goldMuted : 'transparent'}`, cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={isEnabled}
                    onChange={() => {
                      const current = agentSports ? [...agentSports] : [...selectableSports];
                      setAgentSports(current.includes(key) ? current.filter(k => k !== key) : [...current, key]);
                    }}
                    style={{ accentColor: colors.gold, cursor: 'pointer' }}
                  />
                  <span style={{ fontSize: '0.85rem' }}>{cfg.icon}</span>
                  <span style={{ fontSize: '0.8rem' }}>{cfg.name}</span>
                </label>
              );
            })}
          </div>
        )}
        <button onClick={saveAgentSports} disabled={saving} style={saveButtonStyle}>
          {saving ? 'Saving...' : 'Save Sports'}
        </button>
      </div>
    </div>
  );
}
